import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import NoSSR from 'react-no-ssr';
import MediaQuery from 'react-responsive';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { TABLET } from '@spotahome/marketplace-common/src/constants';

import { useListingNavigation } from '../../context/ListingNavigationContext';

import NavigationAngle from './components/NavigationAngle';

import { getItems } from './utils/getItems';
import { scrollList } from './utils/scrollList';
import { showArrowLeft, showArrowRight } from './utils/toggleArrows';

import './ListingContentNavigation.scss';

const getSelectedItemClassNames = (index, selected) =>
  classNames('listing-content-navigation__list-item', {
    'listing-content-navigation__list-item--active': index === selected
  });

export const ListingContentNavigation = ({
  flags = {
    hasReviews: false,
    hasSurroundings: false
  },
  className = null
}) => {
  const [selectedItem, setSelectedItem] = useState(0);
  const [showLeft, setShowLeft] = useState(true);
  const [showRight, setShowRight] = useState(true);

  const wrapperRef = useRef();
  const listRef = useRef();
  const { sectionInView, scrollToSection, setHeight, navigationRef } =
    useListingNavigation();
  const items = getItems(flags, false);

  const handleNavClick = side => () => {
    const offset = side === 'left' ? -1 : 1;

    const current = items[selectedItem];
    const prevItem = items[selectedItem + offset];
    const { section } = prevItem ? prevItem.data : current.data;

    SoyuzAnalytics.sendGA4Event('listing-section-arrow-selected', {
      section
    });

    scrollToSection(section);
  };

  const handleDisplayArrow = () => {
    const list = listRef.current;
    const wrapper = wrapperRef.current;

    setShowLeft(showArrowLeft({ list, wrapper }));
    setShowRight(showArrowRight({ list, wrapper }));
  };

  const handleItemClick = section => () => {
    SoyuzAnalytics.sendGA4Event('listing-section-selected', {
      section
    });
    scrollToSection(section);
  };

  const handleScroll = () => {
    scrollList({
      wrapper: wrapperRef.current,
      list: listRef.current,
      item: items[selectedItem].ref.current
    });
  };

  const resizeHandler = () => {
    if (navigationRef.current) {
      const { height: navHeight } =
        navigationRef.current.getBoundingClientRect();
      setHeight(navHeight);
    }
  };

  useEffect(() => {
    if (selectedItem !== sectionInView) {
      const next = items.findIndex(item => item.data.section === sectionInView);

      if (next >= 0) {
        setSelectedItem(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionInView]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    resizeHandler();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  useEffect(() => {
    handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  useEffect(() => {
    window.addEventListener('resize', handleDisplayArrow);
    handleDisplayArrow();
    handleScroll();

    return () => {
      window.removeEventListener('resize', handleDisplayArrow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wrapperClassName = classNames('listing-content-navigation', className);

  return (
    <div className={wrapperClassName} ref={navigationRef}>
      <NoSSR>
        <MediaQuery maxWidth={TABLET - 1}>
          {showLeft && (
            <NavigationAngle side="left" onClick={handleNavClick('left')} />
          )}
          {showRight && (
            <NavigationAngle side="right" onClick={handleNavClick('right')} />
          )}
        </MediaQuery>
      </NoSSR>
      <div className="listing-content-navigation__wrapper">
        <div
          ref={wrapperRef}
          className="listing-content-navigation__inner-wrapper"
        >
          <ul
            ref={listRef}
            className="listing-content-navigation__list"
            onScroll={handleDisplayArrow}
          >
            {items.map((item, index) => {
              const { section, textKey } = item.data;
              return (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                <li
                  ref={item.ref}
                  key={section}
                  className={getSelectedItemClassNames(index, selectedItem)}
                  onClick={handleItemClick(section)}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex="0"
                >
                  {trans(textKey)}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

ListingContentNavigation.propTypes = {
  flags: PropTypes.shape({
    hasReviews: PropTypes.bool,
    hasSurroundings: PropTypes.bool
  }),
  className: PropTypes.string
};

export default ListingContentNavigation;
